import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Button,
  Divider
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  ExitToApp as LogoutIcon,
  People as AgentsIcon,
  Phone as CallsIcon,
  Assignment as LeadsIcon,
  Assessment as QADashboardIcon,
  Analytics as QAMetricsIcon,
  RateReview as CallAnalysisIcon,
  Psychology as AIModelsIcon,
  QuestionAnswer as AIPromptsIcon,
  AccountCircle as UsersIcon,
  Business as TenantsIcon,
  Settings as SettingsIcon,
  Reviews as QAReviewIcon,
  Api as ApiIcon,
  AccountBalance as BillingIcon,
  Send as LeadSubmissionsIcon,
  AccessTime as AccessTimeIcon,
  TrendingUp as PredictionsIcon,
  AssignmentInd as AgentDashboardIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

const drawerWidth = 240;

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const isQAUser = user?.role === 'qa-user' || user?.role === 'qa-manager' || isAdmin;
  const isQAManager = user?.role === 'qa-manager' || isAdmin;

  const drawer = (
    <div>
      <Toolbar>
        <Typography variant="h6" noWrap component="div">
          Qualitron
        </Typography>
      </Toolbar>
      <List>
        <ListItem button onClick={() => navigate('/dashboard')}>
          <ListItemIcon>
            <DashboardIcon />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItem>
        
        <ListItem button onClick={() => navigate('/agents')}>
          <ListItemIcon>
            <AgentsIcon />
          </ListItemIcon>
          <ListItemText primary="Agents" />
        </ListItem>
        
        <ListItem button onClick={() => navigate('/calls')}>
          <ListItemIcon>
            <CallsIcon />
          </ListItemIcon>
          <ListItemText primary="Calls" />
        </ListItem>
        
        <ListItem button onClick={() => navigate('/leads')}>
          <ListItemIcon>
            <LeadsIcon />
          </ListItemIcon>
          <ListItemText primary="Leads" />
        </ListItem>
        
        <ListItem button onClick={() => navigate('/agent-dashboard')}>
          <ListItemIcon>
            <AgentDashboardIcon />
          </ListItemIcon>
          <ListItemText primary="Agent Dashboard" />
        </ListItem>
        
        {isQAUser && (
          <>
            <Divider />
            <Typography variant="overline" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
              Quality Assurance
            </Typography>
            
            <ListItem button onClick={() => navigate('/qa-dashboard')}>
              <ListItemIcon>
                <QADashboardIcon />
              </ListItemIcon>
              <ListItemText primary="QA Dashboard" />
            </ListItem>
            
            <ListItem button onClick={() => navigate('/call-analysis')}>
              <ListItemIcon>
                <CallAnalysisIcon />
              </ListItemIcon>
              <ListItemText primary="Call Analysis" />
            </ListItem>
            
            <ListItem button onClick={() => navigate('/qa-metrics')}>
              <ListItemIcon>
                <QAMetricsIcon />
              </ListItemIcon>
              <ListItemText primary="QA Metrics" />
            </ListItem>
            
            <ListItem button onClick={() => navigate('/qa-review')}>
              <ListItemIcon>
                <QAReviewIcon />
              </ListItemIcon>
              <ListItemText primary="QA Review" />
            </ListItem>
            
            <ListItem button onClick={() => navigate('/predictions')}>
              <ListItemIcon>
                <PredictionsIcon />
              </ListItemIcon>
              <ListItemText primary="Call Predictions" />
            </ListItem>
          </>
        )}
        
        {(isQAManager || isAdmin) && (
          <>
            <Divider />
            <Typography variant="overline" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
              AI & Models
            </Typography>
            
            <ListItem button onClick={() => navigate('/ai-models')}>
              <ListItemIcon>
                <AIModelsIcon />
              </ListItemIcon>
              <ListItemText primary="AI Models" />
            </ListItem>
            
            <ListItem button onClick={() => navigate('/ai-prompts')}>
              <ListItemIcon>
                <AIPromptsIcon />
              </ListItemIcon>
              <ListItemText primary="AI Prompts" />
            </ListItem>
          </>
        )}
        
        {isAdmin && (
          <>
            
            <Divider />
            <Typography variant="overline" sx={{ px: 2, py: 1, color: 'text.secondary' }}>
              Administration
            </Typography>
            
            <ListItem button onClick={() => navigate('/users')}>
              <ListItemIcon>
                <UsersIcon />
              </ListItemIcon>
              <ListItemText primary="Users" />
            </ListItem>
            
            <ListItem button onClick={() => navigate('/tenants')}>
              <ListItemIcon>
                <TenantsIcon />
              </ListItemIcon>
              <ListItemText primary="Tenants" />
            </ListItem>
            
            <ListItem button onClick={() => navigate('/service-providers')}>
              <ListItemIcon>
                <ApiIcon />
              </ListItemIcon>
              <ListItemText primary="Service Providers" />
            </ListItem>
            
            <ListItem button onClick={() => navigate('/lead-submissions')}>
              <ListItemIcon>
                <LeadSubmissionsIcon />
              </ListItemIcon>
              <ListItemText primary="Lead Submissions" />
            </ListItem>
            
            <ListItem button onClick={() => navigate('/scheduled-submissions')}>
              <ListItemIcon>
                <AccessTimeIcon />
              </ListItemIcon>
              <ListItemText primary="Scheduled Submissions" />
            </ListItem>
            
            <ListItem button onClick={() => navigate('/billing')}>
              <ListItemIcon>
                <BillingIcon />
              </ListItemIcon>
              <ListItemText primary="Billing" />
            </ListItem>
            
            <Divider />
            
            <ListItem button onClick={() => navigate('/settings')}>
              <ListItemIcon>
                <SettingsIcon />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItem>
          </>
        )}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {user?.name || 'Dashboard'}
          </Typography>
          <Button color="inherit" onClick={handleLogout} startIcon={<LogoutIcon />}>
            Logout
          </Button>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="mailbox folders"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
};

export default Layout;